/* ==========================================================
   POKEPLUG — UNIVERSAL SOLD STATE v1.1
   Sitewide inventory-state controller
   ========================================================== */

(() => {
  'use strict';

  const CARD_SELECTORS = [
    '[data-pp-product-card]',
    '[data-pc-card]',
    '[data-ppc-card]',
    '.plug-card',
    '.ppc-product-card',
    '.pp-product-card'
  ].join(',');

  const MEDIA_SELECTORS = [
    '[data-pp-product-media]',
    '.plug-card__media',
    '.ppc-product-card__media',
    '.pp-product-card__image',
    '.pp-product-card__media',
    '.pp-showcase-card__media'
  ].join(',');

  const VARIANT_SELECTORS = [
    '[data-pp-variant-select]',
    '[data-pc-variant-select]',
    '[data-ppc-variant-select]',
    '[data-pp-variant]'
  ].join(',');

  function parseBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (value == null) return null;

    const normalized = String(value).trim().toLowerCase();

    if (['true', '1', 'yes'].includes(normalized)) return true;
    if (['false', '0', 'no'].includes(normalized)) return false;

    return null;
  }

  function getCard(element) {
    return element?.closest?.(CARD_SELECTORS) || null;
  }

  function getMedia(card) {
    if (!card) return null;
    return card.querySelector(MEDIA_SELECTORS);
  }

  function isComingSoon(card) {
    if (!card) return false;

    if (
      card.dataset.comingSoon === 'true' ||
      card.dataset.ppComingSoon === 'true'
    ) {
      return true;
    }

    const template = (
      card.dataset.template ||
      card.dataset.productTemplate ||
      ''
    ).toLowerCase();

    return template.includes('coming-soon');
  }

  function getSelectedVariantControl(card) {
    if (!card) return null;

    const select = card.querySelector(
      '[data-pp-variant-select], [data-pc-variant-select], [data-ppc-variant-select]'
    );

    if (select?.tagName === 'SELECT') {
      return select.options[select.selectedIndex] || null;
    }

    return (
      card.querySelector('[data-pp-variant]:checked') ||
      card.querySelector('[data-pp-variant][checked]') ||
      card.querySelector('[data-pp-variant]')
    );
  }

  function getAvailabilityFromCard(card) {
    if (!card) return null;

    const control = getSelectedVariantControl(card);

    if (control) {
      const parsed = parseBoolean(
        control.dataset.available ??
        control.getAttribute('data-available')
      );

      if (parsed !== null) return parsed;
    }

    const cardValue = parseBoolean(
      card.dataset.available ??
      card.getAttribute('data-available')
    );

    if (cardValue !== null) return cardValue;

    return null;
  }

  function buildState() {
    const state = document.createElement('div');

    state.className = 'pp-sold-state';
    state.dataset.ppSoldState = '';
    state.dataset.ppAvailable = 'true';
    state.hidden = true;
    state.setAttribute('aria-hidden', 'true');

    state.innerHTML = `
      <div class="pp-sold-state__shade" aria-hidden="true"></div>
      <div class="pp-sold-state__content">
        <div class="pp-sold-state__warning" aria-hidden="true">
          <svg viewBox="0 0 64 58" focusable="false">
            <path
              d="M32 4.5L60 53.5H4L32 4.5Z"
              fill="rgba(8,10,18,.72)"
              stroke="currentColor"
              stroke-width="4"
              stroke-linejoin="round"
            ></path>
            <path
              d="M32 19V36"
              stroke="currentColor"
              stroke-width="5"
              stroke-linecap="round"
            ></path>
            <circle cx="32" cy="44" r="2.8" fill="currentColor"></circle>
          </svg>
        </div>
        <strong class="pp-sold-state__title">SOLD OUT</strong>
        <span class="pp-sold-state__message">RESTOCK REQUESTS AVAILABLE</span>
      </div>
    `;

    return state;
  }

  function ensureState(card) {
    if (!card || isComingSoon(card)) return null;

    let state = card.querySelector('[data-pp-sold-state]');
    if (state) return state;

    const media = getMedia(card);
    if (!media) return null;

    state = buildState();
    media.appendChild(state);

    return state;
  }

  function setSoldState(card, available) {
    if (!card) return;

    if (isComingSoon(card)) {
      const existing = card.querySelector('[data-pp-sold-state]');
      if (existing) existing.hidden = true;
      card.classList.remove('pp-sold-card-active');
      return;
    }

    const state = ensureState(card);
    if (!state) return;

    const sold = available === false;

    state.hidden = !sold;
    state.classList.toggle('is-active', sold);
    state.setAttribute('aria-hidden', sold ? 'false' : 'true');
    state.dataset.ppAvailable = sold ? 'false' : 'true';

    card.classList.toggle('pp-sold-card-active', sold);
  }

  function initializeCard(card) {
    if (!card || isComingSoon(card)) return;

    const media = getMedia(card);
    if (!media) return;

    media.classList.add('pp-sold-host');

    const state = ensureState(card);
    if (!state) return;

    const availability = getAvailabilityFromCard(card);

    if (availability !== null) {
      setSoldState(card, availability);
      return;
    }

    const initiallySold =
      !state.hidden &&
      state.classList.contains('is-active');

    card.classList.toggle(
      'pp-sold-card-active',
      initiallySold
    );
  }

  function initializeAll(root = document) {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll(CARD_SELECTORS).forEach(initializeCard);

    if (root.matches?.(CARD_SELECTORS)) {
      initializeCard(root);
    }
  }

  document.addEventListener('change', (event) => {
    const control = event.target.closest?.(VARIANT_SELECTORS);
    if (!control) return;

    const card = getCard(control);
    if (!card) return;

    const availability = getAvailabilityFromCard(card);

    if (availability !== null) {
      setSoldState(card, availability);
    }
  });

  document.addEventListener('pp:variant-change', (event) => {
    const card = event.detail?.card;
    if (!card) return;

    let available = parseBoolean(event.detail.available);

    if (available === null) {
      available = getAvailabilityFromCard(card);
    }

    if (available !== null) {
      setSoldState(card, available);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  let mutationTimer = null;

  const observer = new MutationObserver((mutations) => {
    let relevant = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        if (
          node.matches?.(CARD_SELECTORS) ||
          node.querySelector?.(CARD_SELECTORS)
        ) {
          relevant = true;
          break;
        }
      }

      if (relevant) break;
    }

    if (!relevant) return;

    clearTimeout(mutationTimer);

    mutationTimer = window.setTimeout(() => {
      initializeAll(document);
    }, 40);
  });

  function start() {
    initializeAll(document);

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.PokePlugSoldState = {
    init: initializeAll,
    update: setSoldState
  };
})();